import type { QueryFunctionContext } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import { ShopifyService } from 'services/shopify';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import { convertToCountryCodeEnum } from 'utils/convertToCountryCodeEnum';
import { mapShopifyProduct } from 'utils/mappers/mapShopifyProduct';

export const SHOPIFY_PRODUCT_QUERY = 'shopifyProduct';

export type ShopifyProductQueryKey = [string, string, string];

export const fetchShopifyProduct = async ({
  queryKey,
}: QueryFunctionContext<ShopifyProductQueryKey>): Promise<ShopifyProduct> => {
  const [, handle, countryCode] = queryKey;

  const { productByHandle } = await ShopifyService.getProductSingle({
    handle,
    country: convertToCountryCodeEnum(countryCode),
  });

  if (!productByHandle) {
    throw new Error(`Product with handle "${handle}" not found`);
  }

  return mapShopifyProduct(productByHandle);
};

export const useShopifyProduct = (handle: string) => {
  const {
    value: { countryCode },
    isLoading: isLocaleLoading,
  } = useLocalStorage(UserLocale);

  const query = useQuery({
    queryKey: [SHOPIFY_PRODUCT_QUERY, handle, countryCode],
    queryFn: fetchShopifyProduct,
    enabled: !isLocaleLoading,
  });

  return {
    data: query.data,
    error: query.error,
    isLoading: isLocaleLoading || query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
  };
};
